/**
 * Derive user-facing sync status for conversation list/detail queries.
 */

import { fingerprintConversationDetail } from "./conversationCache.js";

export const SYNC_VARIANT = {
  LOADING: "loading",
  REFRESHING: "refreshing",
  CACHED_STALE: "cached_stale",
  ERROR: "error",
  UP_TO_DATE: "up_to_date",
  UPDATED: "updated",
};

/**
 * @param {Object} params
 * @param {*} params.data
 * @param {boolean} params.isLoading
 * @param {boolean} params.isFetching
 * @param {boolean} params.isError
 * @param {boolean} params.hadCachedData
 * @param {string|null} params.transientNotice
 * @returns {{ variant: string|null, message: string|null, showRetry: boolean }}
 */
export function deriveConversationSyncStatus({
  data,
  isLoading,
  isFetching,
  isError,
  hadCachedData,
  transientNotice = null,
}) {
  const hasData = data != null;

  if (transientNotice === SYNC_VARIANT.UPDATED) {
    return {
      variant: SYNC_VARIANT.UPDATED,
      message: "Updated",
      showRetry: false,
    };
  }

  if (transientNotice === SYNC_VARIANT.UP_TO_DATE) {
    return {
      variant: SYNC_VARIANT.UP_TO_DATE,
      message: "Up to date",
      showRetry: false,
    };
  }

  if (!hasData && isLoading) {
    return {
      variant: SYNC_VARIANT.LOADING,
      message: "Loading conversation…",
      showRetry: false,
    };
  }

  if (!hasData && isError) {
    return {
      variant: SYNC_VARIANT.ERROR,
      message: "Failed to load conversation",
      showRetry: true,
    };
  }

  if (hasData && isFetching) {
    return {
      variant: SYNC_VARIANT.REFRESHING,
      message: hadCachedData ? "Checking for updates…" : "Loading conversation…",
      showRetry: false,
    };
  }

  if (hasData && isError) {
    return {
      variant: SYNC_VARIANT.CACHED_STALE,
      message: "Showing saved copy — couldn't refresh",
      showRetry: true,
    };
  }

  return { variant: null, message: null, showRetry: false };
}

/**
 * @param {Object} params
 * @param {*} params.data
 * @param {boolean} params.isLoading
 * @param {boolean} params.isFetching
 * @param {boolean} params.isError
 * @param {boolean} params.hadCachedData
 * @returns {{ variant: string|null, message: string|null, showRetry: boolean }}
 */
export function deriveConversationListSyncStatus({
  data,
  isLoading,
  isFetching,
  isError,
  hadCachedData,
  transientNotice = null,
}) {
  const hasRows = Array.isArray(data?.conversations) && data.conversations.length > 0;
  const hasPayload = data != null;

  if (transientNotice === SYNC_VARIANT.UPDATED) {
    return { variant: SYNC_VARIANT.UPDATED, message: "List updated", showRetry: false };
  }

  if (transientNotice === SYNC_VARIANT.UP_TO_DATE) {
    return { variant: SYNC_VARIANT.UP_TO_DATE, message: "Up to date", showRetry: false };
  }

  if (!hasPayload && isLoading) {
    return { variant: SYNC_VARIANT.LOADING, message: "Loading conversations…", showRetry: false };
  }

  if (!hasPayload && isError) {
    return { variant: SYNC_VARIANT.ERROR, message: "Failed to load conversations", showRetry: true };
  }

  if (hasPayload && isFetching) {
    return {
      variant: SYNC_VARIANT.REFRESHING,
      message: hadCachedData ? "Checking for updates…" : "Loading conversations…",
      showRetry: false,
    };
  }

  if (hasPayload && isError) {
    return {
      variant: SYNC_VARIANT.CACHED_STALE,
      message: "Showing saved list — couldn't refresh",
      showRetry: true,
    };
  }

  if (!hasRows && hasPayload && !isFetching && !isError) {
    return { variant: null, message: null, showRetry: false };
  }

  return { variant: null, message: null, showRetry: false };
}

export function fingerprintConversationList(data) {
  if (!data) return null;

  const conversations = Array.isArray(data.conversations) ? data.conversations : [];
  return JSON.stringify({
    total: data.pagination?.total ?? conversations.length,
    count: conversations.length,
    firstPhone: conversations[0]?.customerPhone ?? null,
    lastActivity: conversations[0]?.lastActivityAt ?? null,
    n8nError: data.n8nError?.code ?? null,
  });
}

export { fingerprintConversationDetail };
